import { useState, useEffect } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { migrateLocalStorageToCloud, ensureUserProfile } from "@/utils/cloudSync";
import { toast } from "@/hooks/use-toast";

interface UserProfile {
  display_name: string | null;
  created_at: string;
}

interface GameStats {
  total_played: number;
  total_won: number;
  current_streak: number;
  max_streak: number;
  guess_distribution?: any;
  updated_at?: string;
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [gameStats, setGameStats] = useState<GameStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Helper to fetch game stats
  const fetchGameStats = async (userId: string) => {
    setStatsLoading(true);
    
    const { data, error } = await supabase
      .from("game_stats")
      .select("total_played, total_won, current_streak, max_streak, guess_distribution, updated_at")
      .eq("user_id", userId)
      .maybeSingle();
    
    if (data) setGameStats(data);
    if (error) console.error("Error fetching stats:", error);
    
    // Auto-recalculation: Check for stats mismatches with timestamp-based throttling
    const lastCheck = localStorage.getItem("wordpop-stats-last-check-v2");
    const now = Date.now();
    const ONE_DAY = 24 * 60 * 60 * 1000;
    const shouldCheck = !lastCheck || (now - parseInt(lastCheck)) > ONE_DAY;
    
    if (data && shouldCheck) {
      // Count actual game_progress entries
      const { count: actualPlayed } = await supabase
        .from("game_progress")
        .select("*", { count: "exact", head: true })
        .eq("user_id", userId)
        .eq("completed", true);
      
      // Always record that we checked
      localStorage.setItem("wordpop-stats-last-check-v2", now.toString());
      
      // If mismatch detected, recalculate immediately
      if (actualPlayed !== null && actualPlayed !== data.total_played) {
        console.log(`📊 Stats mismatch detected: game_stats=${data.total_played}, actual=${actualPlayed}. Recalculating...`);
        
        const { error: recalcError } = await supabase.rpc("recalculate_user_stats", {
          p_user_id: userId
        });
        
        if (!recalcError) {
          // Refetch stats after recalculation
          const { data: updatedStats } = await supabase
            .from("game_stats")
            .select("total_played, total_won, current_streak, max_streak, guess_distribution")
            .eq("user_id", userId)
            .maybeSingle();
          
          if (updatedStats) {
            setGameStats(updatedStats);
            toast({
              title: "Stats synchronized!",
              description: `Corrected: ${actualPlayed} games played, ${updatedStats.total_won} won.`,
            });
          }
        } else {
          console.error("Error recalculating stats:", recalcError);
        }
      }
    }
    
    setStatsLoading(false);
  };

  useEffect(() => {
    // Set up auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        // Fetch profile when user changes
        if (session?.user) {
          setTimeout(async () => {
            // Ensure profile exists before fetching
            await ensureUserProfile(session.user.id);
            
            const { data } = await supabase
              .from("profiles")
              .select("display_name, created_at")
              .eq("id", session.user.id)
              .maybeSingle();
            
            if (data) setProfile(data);
            
            // Fetch game stats
            fetchGameStats(session.user.id);

            // Handle migration on first sign in
            if (event === "SIGNED_IN") {
              const hasMigrated = localStorage.getItem("wordpop-cloud-migrated");
              if (!hasMigrated) {
                migrateLocalStorageToCloud(session.user.id)
                  .then(() => {
                    localStorage.setItem("wordpop-cloud-migrated", "true");
                    toast({
                      title: "Progress synced!",
                      description: "Your local games have been saved to the cloud.",
                    });
                    // Refetch stats after migration
                    fetchGameStats(session.user.id);
                  })
                  .catch((error) => {
                    console.error("Migration error:", error);
                  });
              }
            }
          }, 0);
        } else {
          setProfile(null);
          setGameStats(null);
        }
      }
    );

    // Then check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      // Fetch profile for existing session
      if (session?.user) {
        supabase
          .from("profiles")
          .select("display_name, created_at")
          .eq("id", session.user.id)
          .maybeSingle()
          .then(({ data }) => {
            if (data) setProfile(data);
          });
        
        // Fetch game stats
        fetchGameStats(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setGameStats(null);
  };

  const updateProfile = (newDisplayName: string) => {
    if (profile) {
      setProfile({ ...profile, display_name: newDisplayName });
    }
  };

  const refreshStats = () => {
    if (user) {
      fetchGameStats(user.id);
    }
  };

  return {
    user,
    session,
    loading,
    profile,
    gameStats,
    statsLoading,
    signOut,
    updateProfile,
    refreshStats,
    isAuthenticated: !!user
  };
};
